import React, { useState, useEffect, useCallback } from 'react';

const AgentSpend = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('week');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentDetail, setAgentDetail] = useState(null);
  const [pricing, setPricing] = useState([]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/agent-tokens/stats?timeframe=${timeframe}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch stats');
      const data = await res.json();
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [timeframe]);

  const fetchPricing = useCallback(async () => {
    try {
      const res = await fetch('/api/agent-tokens/pricing', { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setPricing(data.pricing || []);
    } catch (err) {
      // Pricing is optional
    }
  }, []);

  const fetchAgentDetail = useCallback(async (name) => {
    try {
      const res = await fetch(`/api/agent-tokens/agent/${name}?timeframe=${timeframe}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch agent detail');
      const data = await res.json();
      setAgentDetail(data);
    } catch (err) {
      setError(err.message);
    }
  }, [timeframe]);

  useEffect(() => {
    fetchStats();
    fetchPricing();
  }, [fetchStats, fetchPricing]);

  useEffect(() => {
    if (selectedAgent) {
      fetchAgentDetail(selectedAgent);
    } else {
      setAgentDetail(null);
    }
  }, [selectedAgent, fetchAgentDetail]);

  const fmtCost = (usd) => {
    if (usd === null || usd === undefined) return '$0.00';
    if (usd < 0.01) return '<$0.01';
    return `$${Number(usd).toFixed(2)}`;
  };

  const fmtTokens = (n) => {
    if (!n) return '0';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
  };

  const timeframes = [
    { key: 'hour', label: '1 Hour' },
    { key: 'day', label: 'Today' },
    { key: 'week', label: 'This Week' },
    { key: 'month', label: 'This Month' },
    { key: 'all', label: 'All Time' },
  ];

  const maxDailyCost = stats?.timeline?.length > 0
    ? Math.max(...stats.timeline.map(d => Number(d.cost) || 0), 0.01)
    : 0.01;

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ margin: 0 }}>💰 Agent Spend Tracking</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          {timeframes.map(tf => (
            <button
              key={tf.key}
              className={`btn btn-sm ${timeframe === tf.key ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setTimeframe(tf.key)}
            >
              {tf.label}
            </button>
          ))}
          <a
            className="btn btn-sm btn-secondary"
            href={`/api/agent-tokens/export?timeframe=${timeframe}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none' }}
          >
            📥 Export CSV
          </a>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Summary stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <StatCard
          label="Total Cost"
          value={fmtCost(stats?.summary?.total_cost)}
          sub={`${stats?.summary?.request_count || 0} requests`}
          color="#3b82f6"
        />
        <StatCard
          label="Total Tokens"
          value={fmtTokens(stats?.summary?.total_tokens)}
          sub={`${fmtTokens(stats?.summary?.input_tokens)} in / ${fmtTokens(stats?.summary?.output_tokens)} out`}
          color="#10b981"
        />
        <StatCard
          label="Avg Cost / Request"
          value={fmtCost(stats?.summary?.avg_cost_per_request)}
          sub={`${fmtTokens(stats?.summary?.avg_tokens_per_request)} avg tokens`}
          color="#f59e0b"
        />
        <StatCard
          label="Fleet Size"
          value={stats?.agents?.length || 0}
          sub={`${stats?.agents?.filter(a => a.customer_facing).length || 0} customer-facing`}
          color="#8b5cf6"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedAgent ? '1fr 1fr' : '1fr', gap: '20px' }}>
        {/* Per-agent table */}
        <div className="card" style={{ overflow: 'hidden' }}>
          <div className="card-header" style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
            <h3 style={{ margin: 0 }}>Agent Fleet</h3>
          </div>
          <table className="table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Agent</th>
                <th>Role</th>
                <th>Model</th>
                <th>IP</th>
                <th>Tokens</th>
                <th>Cost</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>Loading...</td></tr>
              ) : stats?.agents?.length > 0 ? (
                stats.agents.map(a => {
                  const isCustomer = a.customer_facing;
                  const statusColor = a.fleet_status === 'online' ? '#22c55e' : a.fleet_status === 'error' ? '#ef4444' : '#9ca3af';
                  return (
                    <tr
                      key={a.agent_name}
                      onClick={() => setSelectedAgent(a.agent_name === selectedAgent ? null : a.agent_name)}
                      style={{ cursor: 'pointer', background: a.agent_name === selectedAgent ? '#eff6ff' : undefined }}
                    >
                      <td>
                        <strong>{a.display_name || a.agent_name}</strong>
                        {isCustomer && (
                          <span style={{ marginLeft: '6px', fontSize: '10px', background: '#8b5cf6', color: 'white', padding: '1px 6px', borderRadius: '8px' }}>
                            👤 {a.customer_name}
                          </span>
                        )}
                        {a.telegram_bot && (
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>{a.telegram_bot}</div>
                        )}
                      </td>
                      <td><span style={{ fontSize: '12px' }}>{roleIcon(a.role)} {a.role}</span></td>
                      <td style={{ fontSize: '12px' }}>{a.fleet_model || '—'}</td>
                      <td style={{ fontSize: '12px', color: '#6b7280' }}>{a.ip_address || '—'}</td>
                      <td>{fmtTokens(a.total_tokens)}</td>
                      <td style={{ fontWeight: 600, color: '#059669' }}>{fmtCost(a.total_cost)}</td>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusColor, display: 'inline-block' }} />
                          <span style={{ fontSize: '12px' }}>{a.fleet_status || 'offline'}</span>
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>
                  No agents registered.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Agent detail panel */}
        {selectedAgent && agentDetail && (
          <div className="card" style={{ overflow: 'hidden' }}>
            <div className="card-header" style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>📊 {selectedAgent}</h3>
              <button className="btn btn-sm btn-ghost" onClick={() => setSelectedAgent(null)}>×</button>
            </div>
            <div style={{ padding: '16px' }}>
              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                <MiniStat label="Total Cost" value={fmtCost(agentDetail.stats?.total_cost)} />
                <MiniStat label="Total Tokens" value={fmtTokens(agentDetail.stats?.total_tokens)} />
                <MiniStat label="Requests" value={agentDetail.stats?.request_count || 0} />
                <MiniStat label="Avg / Request" value={fmtCost(agentDetail.stats?.total_cost / agentDetail.stats?.request_count)} />
              </div>

              {/* Models used */}
              {agentDetail.models?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '13px', color: '#6b7280', marginBottom: '6px' }}>MODELS</h4>
                  {agentDetail.models.map(m => (
                    <div key={m.model} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '14px' }}>
                      <span>{m.model}</span>
                      <span style={{ color: '#059669', fontWeight: 600 }}>{fmtCost(m.total_cost)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Daily timeline bar chart */}
              {agentDetail.timeline?.length > 0 && (
                <div>
                  <h4 style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>DAILY COST</h4>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '80px', overflowX: 'auto' }}>
                    {agentDetail.timeline.map((d, i) => {
                      const heightPct = (Number(d.cost) / maxDailyCost) * 100;
                      return (
                        <div
                          key={i}
                          title={`${d.date}: ${fmtCost(d.cost)} (${d.requests} reqs)`}
                          style={{
                            minWidth: '20px',
                            height: `${Math.max(heightPct, 2)}%`,
                            background: '#3b82f6',
                            borderRadius: '3px 3px 0 0',
                            opacity: 0.8,
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Per-model breakdown */}
      <div className="card" style={{ marginTop: '20px', overflow: 'hidden' }}>
        <div className="card-header" style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: 0 }}>Per-Model Cost</h3>
        </div>
        <table className="table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Model</th>
              <th>Requests</th>
              <th>Input Tokens</th>
              <th>Output Tokens</th>
              <th>Total Tokens</th>
              <th>Cost</th>
              <th>Avg Cost/Req</th>
            </tr>
          </thead>
          <tbody>
            {stats?.models?.length > 0 ? (
              stats.models.map(m => (
                <tr key={m.model}>
                  <td><strong>{m.model}</strong></td>
                  <td>{m.request_count}</td>
                  <td>{fmtTokens(m.input_tokens)}</td>
                  <td>{fmtTokens(m.output_tokens)}</td>
                  <td>{fmtTokens(m.total_tokens)}</td>
                  <td style={{ fontWeight: 600, color: '#059669' }}>{fmtCost(m.total_cost)}</td>
                  <td>{fmtCost(m.total_cost / m.request_count)}</td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="7" style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>No data</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Model pricing table */}
      <div className="card" style={{ marginTop: '20px', overflow: 'hidden' }}>
        <div className="card-header" style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: 0 }}>Model Pricing (USD per 1M tokens)</h3>
        </div>
        <table className="table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Model</th>
              <th>Provider</th>
              <th>Input $/MTok</th>
              <th>Output $/MTok</th>
            </tr>
          </thead>
          <tbody>
            {pricing.length > 0 ? (
              pricing.map(p => (
                <tr key={p.model}>
                  <td><strong>{p.model}</strong></td>
                  <td>{p.provider}</td>
                  <td>${p.input_cost_per_mtok.toFixed(2)}</td>
                  <td>${p.output_cost_per_mtok.toFixed(2)}</td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>No pricing data</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Helper components ─────────────────────────────────────────────────

const ROLE_ICONS = {
  orchestrator: '🧠',
  network: '🌐',
  support: '🛟',
  sales: '💼',
  content: '✍️',
  infra: '🏗️',
  qc: '🔍',
  customer: '👤',
};

function roleIcon(role) {
  return ROLE_ICONS[role] || '🤖';
}

const StatCard = ({ label, value, sub, color }) => (
  <div className="card" style={{ padding: '16px', borderLeft: `4px solid ${color}` }}>
    <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
    <div style={{ fontSize: '28px', fontWeight: 700, margin: '4px 0' }}>{value}</div>
    <div style={{ fontSize: '12px', color: '#9ca3af' }}>{sub}</div>
  </div>
);

const MiniStat = ({ label, value }) => (
  <div style={{ padding: '8px 12px', background: '#f9fafb', borderRadius: '6px' }}>
    <div style={{ fontSize: '11px', color: '#6b7280' }}>{label}</div>
    <div style={{ fontSize: '18px', fontWeight: 600 }}>{value}</div>
  </div>
);

export default AgentSpend;
